
import { Link } from 'react-router-dom';

export default function GettingStartedSection() {
  return (
    <section className="mb-16">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Get Started With Monthly Bookkeeping in El Paso TX</h3>
      <p className="text-gray-600 leading-relaxed mb-8">
        Convinced that monthly bookkeeping is the right move for your business? Here is a practical, step-by-step framework to transition from sporadic record-keeping to a reliable monthly routine. Whether you handle it in-house or partner with a professional <strong>El Paso</strong> bookkeeping service, these steps will set you up for success.
      </p>

      <div className="grid md:grid-cols-2 gap-8 items-start mb-12">
        <div className="space-y-6">
          {[
            {
              step: "Step 1",
              title: "Separate Personal and Business Finances",
              desc: "If you have not already, open a dedicated business checking account and credit card. This single action eliminates the most common source of bookkeeping chaos for El Paso small business owners.",
              color: "bg-teal-100 text-teal-700",
            },
            {
              step: "Step 2",
              title: "Choose the Right Accounting Software",
              desc: "QuickBooks Online, Xero, and FreshBooks are all excellent options depending on your industry and transaction volume. The right platform automates bank feeds, invoicing, and report generation.",
              color: "bg-orange-100 text-orange-700",
            },
            {
              step: "Step 3",
              title: "Set a Monthly Close Date",
              desc: "Pick a specific day each month—ideally within the first five business days—to reconcile accounts, categorize transactions, and review reports. Consistency is the key to making monthly bookkeeping a habit rather than a chore.",
              color: "bg-green-100 text-green-700",
            },
            {
              step: "Step 4",
              title: "Digitize All Receipts Immediately",
              desc: "Use a receipt-scanning app connected to your accounting software. Snap a photo the moment you make a purchase. Waiting even a day increases the chance that receipts get lost or forgotten.",
              color: "bg-yellow-100 text-yellow-700",
            },
            {
              step: "Step 5",
              title: "Review Three Key Reports Monthly",
              desc: "Your profit-and-loss statement, balance sheet, and cash-flow statement tell you everything you need to know. If the numbers look confusing, that is a sign you need professional support.",
              color: "bg-red-100 text-red-700",
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4">
              <span className={`${item.color} text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap mt-1`}>
                {item.step}
              </span>
              <div>
                <h4 className="font-bold text-gray-900 mb-1 text-base">
                  <a href={`#step-${i + 1}`} className="hover:text-teal-600 transition-colors">{item.title}</a>
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="w-full h-96 rounded-xl overflow-hidden shadow-lg">
          <img
            src="https://public.readdy.ai/ai/img_res/ce08906663fd6ce0cf287e9c24fdf01c.jpg"
            alt="getting-started-monthly-bookkeeping-el-paso-tx"
            title="Step-by-Step Guide to Monthly Bookkeeping for El Paso TX Businesses"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </div>

      {/* When to Hire a Professional */}
      <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-2xl p-8 border border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">When Should You Hire a Professional Bookkeeper?</h3>
        <p className="text-gray-600 text-center mb-6 max-w-3xl mx-auto text-sm leading-relaxed">
          DIY bookkeeping works for some businesses, but there are clear signals that it is time to bring in a professional. If any of the following apply to your <strong>El Paso TX</strong> business, monthly professional bookkeeping will almost certainly save you money in the long run.
        </p>
        <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {[
            "Your monthly revenue exceeds $10,000",
            "You have employees on payroll",
            "You collect and remit sales tax",
            "You are behind on bank reconciliation",
            "You plan to apply for a loan or line of credit",
            "You dread tax season every year",
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm">
              <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-white text-sm"></i>
              </div>
              <span className="text-gray-700 text-sm">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
